export const SKIP_FIELDS = ['clinicId', 'id', 'createdAt', 'modifiedAt'];

export const PREFERENCE_FIELDS = [
    'callPreference',
    'apptSummary',
    'reminderTime',
    'sameDayReminderTime',
    'daysPrebookReminder',
    'daysFirstReminder',
    'daysSecondReminder',
    'minutesToAppt',
];

export const REMINDER_LABELS = {
    apptReminder: 'Appointment Reminder',
    isPrebookedReminder: 'Prebooked Reminder',
    isFirstReminder: 'First Reminder',
    isSecondReminder: 'Second Reminder',
    isLastReminder: 'Last Reminder',
    isSameDayReminder: 'Same Day Reminder',
    isClReplacement: 'Contact Lens Replacement Reminder',
    isClReorder: 'Contact Lens Reorder',
    isBirthday: 'Birthday Reminder',
    isThankyou: 'Thank You Reminder',
    isWelcome: 'Welcome Reminder',
    isEmail: 'Email Reminder',
    isSms: 'SMS Reminder',
    isCall: 'Call Reminder',
    isGlPickup: 'Glasses Pickup',
    isClPickup: 'Contact Lens Pickup',
    isFollowup: 'Follow Up',
    isApptSummary: 'Appointment Summary',
    isClGlSummary: 'Contact Lens & Glass Summary',
};

export const REMINDER_GROUPS = [
    {
        title: 'Appointment Reminders',
        keys: ['apptReminder', 'isPrebookedReminder', 'isFirstReminder', 'isSecondReminder', 'isLastReminder', 'isSameDayReminder'],
    },
    {
        title: 'Patient Reminders',
        keys: ['isClReplacement', 'isClReorder', 'isBirthday', 'isThankyou', 'isWelcome', 'isFollowup'],
    },
    {
        title: 'Delivery Channels',
        keys: ['isEmail', 'isSms', 'isCall'],
    },
    {
        title: 'Summaries & Pickups',
        keys: ['isApptSummary', 'isClGlSummary', 'isGlPickup', 'isClPickup'],
    },
];

export const getReminderLabel = (key) => REMINDER_LABELS[key] || key;

export const getReminderIconClass = (key) => {
    switch (key) {
        case 'apptReminder':
        case 'isPrebookedReminder':
        case 'isFirstReminder':
        case 'isSecondReminder':
        case 'isLastReminder':
        case 'isSameDayReminder':
            return 'bi-calendar-event';
        case 'isClReplacement':
        case 'isClReorder':
        case 'isClPickup':
            return 'bi-eye';
        case 'isBirthday':
            return 'bi-gift';
        case 'isThankyou':
            return 'bi-heart';
        case 'isWelcome':
            return 'bi-hand-thumbs-up';
        case 'isEmail':
            return 'bi-envelope';
        case 'isSms':
            return 'bi-chat-left-text';
        case 'isCall':
            return 'bi-telephone';
        case 'isGlPickup':
            return 'bi-eyeglasses';
        case 'isFollowup':
            return 'bi-arrow-repeat';
        case 'isApptSummary':
        case 'isClGlSummary':
            return 'bi-file-text';
        default:
            return 'bi-bell';
    }
};

export const getReminderIconTone = (key) => {
    switch (key) {
        case 'apptReminder':
        case 'isPrebookedReminder':
        case 'isFirstReminder':
        case 'isSecondReminder':
        case 'isLastReminder':
        case 'isSameDayReminder':
            return 'appointment';
        case 'isClReplacement':
        case 'isClReorder':
        case 'isClPickup':
            return 'lens';
        case 'isBirthday':
        case 'isThankyou':
        case 'isWelcome':
        case 'isFollowup':
            return 'patient';
        case 'isEmail':
            return 'email';
        case 'isSms':
            return 'sms';
        case 'isCall':
            return 'call';
        case 'isGlPickup':
            return 'pickup';
        case 'isApptSummary':
        case 'isClGlSummary':
            return 'summary';
        default:
            return 'default';
    }
};

export const getReminderEntries = (settings) => {
    if (!settings || typeof settings !== 'object') return [];

    const groupedKeys = new Set(REMINDER_GROUPS.flatMap((group) => group.keys));

    return REMINDER_GROUPS.map((group) => ({
        title: group.title,
        items: group.keys
            .filter((key) => key in settings && !SKIP_FIELDS.includes(key) && !PREFERENCE_FIELDS.includes(key))
            .map((key) => ({ key, value: settings[key] })),
    })).filter((group) => group.items.length > 0);
};

export const countEnabledReminders = (settings) => {
    if (!settings) return { enabled: 0, disabled: 0, total: 0 };

    const toggleEntries = Object.entries(settings).filter(
        ([key]) => !SKIP_FIELDS.includes(key) && !PREFERENCE_FIELDS.includes(key)
    );
    const enabled = toggleEntries.filter(([, value]) => value === true).length;

    return {
        enabled,
        disabled: toggleEntries.length - enabled,
        total: toggleEntries.length,
    };
};

export const getPreferenceText = (key, value) => {
    if (value === null || value === undefined || value === '') {
        return 'Not set';
    }

    switch (key) {
        case 'callPreference':
            switch (value) {
                case 'DISABLED':
                    return 'Disabled';
                case 'HOME_PHONE':
                    return 'Home Phone';
                case 'CELL_PHONE':
                    return 'Cell Phone';
                default:
                    return 'Not set';
            }
        case 'apptSummary':
            switch (value) {
                case 'CLINIC':
                    return 'Clinic';
                case 'ECP':
                    return 'ECP';
                case 'BOTH':
                    return 'Both';
                default:
                    return 'Not set';
            }
        case 'minutesToAppt':
            return `${value} mins`;
        default:
            return String(value);
    }
};

export const PREFERENCE_LABELS = {
    callPreference: 'Call Preference',
    apptSummary: 'Appointment Summary',
    reminderTime: 'Reminder Time',
    sameDayReminderTime: 'Same Day Reminder Time',
    daysPrebookReminder: 'Days to Send Prebook Reminder',
    daysFirstReminder: 'Days to Send First Reminder',
    daysSecondReminder: 'Days to Send Second Reminder',
    minutesToAppt: 'Minutes Earlier',
};
